/**
 * The therapist's view of one map.
 *
 * The board is the same one the patient gets; what is here and not there is
 * management — the share link, and filing an image of the board into the
 * record.
 *
 * The page is one screen tall and does not scroll. A board you scroll past is
 * a board you cannot see while you draw on it: moving around it is the
 * canvas's own job, by dragging. The nav is borrowed on the way in and handed
 * back on the way out, the same courtesy the constellation board takes.
 *
 * `embedded` is the same map framed inside a call. It has the whole frame, no
 * way "back" (the call is what is behind it), and no share link: the patient
 * was sent theirs by the call, and minting another here would end it.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router";
import {
    ArrowLeft,
    Camera,
    Download,
    Eye,
    EyeOff,
    Link2,
    Loader2,
} from "lucide-react";

import Button from "../../../../shared/ui/button/Button";
import ShareLinkDialog, { type ShareLinkStatus } from "../../../../shared/ui/share/ShareLinkDialog";
import { useSidebar } from "../../../../context/useSidebar";
import { boardToPng } from "../../application/boardImage";
import { downloadBlob } from "../../application/useMapSnapshots";
import { useTherapistMap } from "../../application/useSystemicMap";
import MapBoard from "../components/MapBoard";
import MapSnapshots from "../components/MapSnapshots";

/**
 * How big a filed or downloaded board is, along its longer side.
 *
 * Large enough to read three generations of names on a printout, and to zoom
 * into on a screen, without making a five-megabyte file of a line drawing.
 */
const SNAPSHOT_LONG_EDGE = 2400;

export default function TherapistSystemicMapPage({ embedded = false }: { embedded?: boolean }) {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { mapId } = useParams<{ mapId: string }>();
    const map = useTherapistMap(mapId);
    const [shareOpen, setShareOpen] = useState(false);
    const [linkStatus, setLinkStatus] = useState<ShareLinkStatus>("loading");
    const [snapshotSaved, setSnapshotSaved] = useState(false);
    const [sharingWithPatient, setSharingWithPatient] = useState(false);
    const boardRef = useRef<HTMLDivElement | null>(null);
    const { collapseSidebar, restoreSidebar } = useSidebar();

    // The board borrows the nav's width on entry and hands it back on the way
    // out. It does not touch the stored nav preference.
    useEffect(() => {
        collapseSidebar();
        return () => restoreSidebar();
    }, [collapseSidebar, restoreSidebar]);

    /**
     * Mint a link for the share dialog. Minting ends the link before it, so
     * one already in hand is shown as it is rather than replaced.
     */
    const mintLink = useCallback(async () => {
        setLinkStatus("loading");
        try {
            const link = await map.createShareLink();
            if (!link) throw new Error("no link");
            setLinkStatus("ready");
        } catch {
            setLinkStatus("failed");
        }
    }, [map]);

    const openShare = () => {
        setShareOpen(true);
        if (map.shareLink) setLinkStatus("ready");
        else void mintLink();
    };

    const revokeLink = () => {
        void map.revokeShareLink();
        setShareOpen(false);
    };

    // Who is on the board through the link, from the live channel.
    const guests = (map.presence?.members ?? []).filter((member) => member.kind === "participant");

    /**
     * The board as a PNG, rendered from the very SVG on screen.
     *
     * Rendered rather than laid out again, so what is filed — or handed over —
     * is what the therapist was looking at. Preparing the copy is its own job:
     * see `boardImage`.
     */
    const boardImage = useCallback(async (): Promise<Blob | null> => {
        const svg = boardRef.current?.querySelector("svg");
        if (!svg) return null;
        return boardToPng(svg, SNAPSHOT_LONG_EDGE);
    }, []);

    /** File an image of the board into the record, where it can be seen again. */
    const saveSnapshot = useCallback(async () => {
        if (!mapId) return;
        const blob = await boardImage();
        if (!blob) return;

        await map.uploadSnapshot(blob);
        setSnapshotSaved(true);
        setTimeout(() => setSnapshotSaved(false), 2500);
    }, [boardImage, map, mapId]);

    /** Hand the board over as a file, without filing it in the record. */
    const downloadBoard = useCallback(async () => {
        const blob = await boardImage();
        if (!blob) return;
        const name = (map.board?.title || "genograma").replace(/[^\w\s-]/g, "").trim();
        downloadBlob(blob, `${name || "genograma"}.png`);
    }, [boardImage, map.board?.title]);

    // A map about nobody in particular — a workshop board — has no patient
    // whose portal it could appear in. Nor has a group's: it holds every
    // member's family, and the server refuses to hand it to any one of them.
    const hasSubject = Boolean(
        map.board?.patient_id || map.board?.couple_patient_id,
    );
    const sharedWithPatient = Boolean(map.board?.shared_with_patient_at);

    const togglePatientSharing = async () => {
        setSharingWithPatient(true);
        try {
            await map.setPatientSharing(!sharedWithPatient);
        } finally {
            setSharingWithPatient(false);
        }
    };

    if (map.status === "loading" || (!map.state && map.status !== "error")) {
        return (
            <p
                role="status"
                className="flex items-center gap-2 p-8 text-sm text-content-secondary"
            >
                <Loader2
                    size={16}
                    aria-hidden="true"
                    className="animate-spin motion-reduce:animate-none"
                />
                {t("systemicMap.page.loading")}
            </p>
        );
    }

    if (map.status === "error" || !map.state) {
        return (
            <div className="p-8">
                <p role="alert" className="text-sm text-status-critical">
                    {t("systemicMap.page.loadError")}
                </p>
                <Button
                    className="mt-3"
                    variant="outline"
                    size="sm"
                    onClick={() => void map.refresh()}
                >
                    {t("systemicMap.list.retry")}
                </Button>
            </div>
        );
    }

    return (
        <div className={`flex flex-col gap-3 ${embedded ? "h-[100dvh] bg-surface-base p-3" : "h-[calc(100vh-8rem)]"}`}>
            <header className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2">
                <div className="flex min-w-0 items-center gap-2">
                    {!embedded && (
                        <Button
                            size="sm"
                            variant="ghost"
                            startIcon={<ArrowLeft size={16} aria-hidden="true" />}
                            onClick={() => navigate(-1)}
                        >
                            {t("systemicMap.page.back")}
                        </Button>
                    )}
                    <h1 className="truncate text-lg font-semibold text-content-primary">
                        {map.board?.title}
                    </h1>
                    {sharedWithPatient && (
                        // Said in words beside the control, because "is the
                        // family reading this" is not a thing to infer from a
                        // button's shade.
                        <p className="flex shrink-0 items-center gap-1.5 rounded-lg bg-surface-sunken px-2 py-1 text-xs font-medium text-content-secondary">
                            <Eye size={13} aria-hidden="true" />
                            {t("systemicMap.page.visibleToPatient")}
                        </p>
                    )}
                </div>

                <div className="flex flex-wrap items-center gap-2">
                    {snapshotSaved && (
                        <p role="status" className="text-sm text-status-success">
                            {t("systemicMap.page.snapshotSaved")}
                        </p>
                    )}
                    {hasSubject && (
                        <Button
                            size="sm"
                            variant={sharedWithPatient ? "ghost" : "outline"}
                            disabled={sharingWithPatient}
                            startIcon={
                                sharedWithPatient ? (
                                    <EyeOff size={16} aria-hidden="true" />
                                ) : (
                                    <Eye size={16} aria-hidden="true" />
                                )
                            }
                            onClick={() => void togglePatientSharing()}
                        >
                            {sharedWithPatient
                                ? t("systemicMap.page.unshareWithPatient")
                                : t("systemicMap.page.shareWithPatient")}
                        </Button>
                    )}
                    <Button
                        size="sm"
                        variant="outline"
                        startIcon={<Download size={16} aria-hidden="true" />}
                        onClick={() => void downloadBoard()}
                    >
                        {t("systemicMap.page.download")}
                    </Button>
                    <Button
                        size="sm"
                        variant="outline"
                        startIcon={<Camera size={16} aria-hidden="true" />}
                        onClick={() => void saveSnapshot()}
                    >
                        {t("systemicMap.page.snapshot")}
                    </Button>
                    {!embedded && (
                        <Button
                            size="sm"
                            startIcon={<Link2 size={16} aria-hidden="true" />}
                            onClick={openShare}
                        >
                            {t("systemicMap.page.share")}
                        </Button>
                    )}
                </div>
            </header>

            <MapSnapshots
                mapId={mapId}
                count={map.board?.snapshot_urls.length ?? 0}
                onChanged={() => map.refresh()}
            />

            <div ref={boardRef} className="min-h-0 flex-1">
                <MapBoard
                    state={map.state}
                    saveStatus={map.saveStatus}
                    onSubmit={map.submit}
                    onUndo={map.undo}
                    onRedo={map.redo}
                    canUndo={map.canUndo}
                    canRedo={map.canRedo}
                    presence={map.presence}
                    viewer={{ type: "therapist" }}
                />
            </div>

            <ShareLinkDialog
                isOpen={shareOpen}
                onClose={() => setShareOpen(false)}
                title={t("systemicMap.share.title")}
                subtitle={t("systemicMap.share.subtitle")}
                status={linkStatus}
                url={map.shareLink?.url ?? null}
                message={map.shareLink ? t("systemicMap.share.message", { url: map.shareLink.url }) : ""}
                onRetry={() => void mintLink()}
                presence={
                    guests.length > 0
                        ? t("systemicMap.share.connected", { names: guests.map((guest) => guest.name).join(", ") })
                        : t("systemicMap.share.waiting")
                }
                onRevoke={revokeLink}
            />
        </div>
    );
}
